import React, { useState, useEffect, useRef } from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

const sizeMap = {
    sm: 'h-48',
    md: 'h-64',
    lg: 'h-80',
    xl: 'h-96',
};

export default function Carousel({
    items = [],
    renderItem,
    currentIndex = 0,
    onChange,
    showDots = true,
    showArrows = true,
    autoPlay = false,
    interval = 5000,
    size = 'md',
    className = '',
    style,
}) {
    const [activeIndex, setActiveIndex] = useState(currentIndex);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const autoPlayRef = useRef(null);
    const handleNextRef = useRef(null);

    // Sync with controlled prop
    useEffect(() => {
        if (currentIndex !== undefined) {
            setActiveIndex(currentIndex);
        }
    }, [currentIndex]);

    // Auto-play logic
    useEffect(() => {
        if (!autoPlay || items.length === 0) return;

        autoPlayRef.current = setInterval(() => {
            handleNextRef.current?.();
        }, interval);

        return () => clearInterval(autoPlayRef.current);
    }, [autoPlay, interval, items.length]);

    const handlePrev = () => {
        const newIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
        setActiveIndex(newIndex);
        onChange?.(newIndex);
        setIsTransitioning(true);
    };

    const handleNext = () => {
        const newIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
        setActiveIndex(newIndex);
        onChange?.(newIndex);
        setIsTransitioning(true);
    };
    handleNextRef.current = handleNext;

    const handleDotClick = (index) => {
        setActiveIndex(index);
        onChange?.(index);
        setIsTransitioning(true);
    };

    const handleTransitionEnd = () => {
        setIsTransitioning(false);
    };

    if (!items || items.length === 0) {
        return (
            <div className={cx('rounded-lg bg-gray-100 flex items-center justify-center text-gray-500', sizeMap[size], className)}
                style={style}>
                No items to display
            </div>
        );
    }

    const currentItem = items[activeIndex];

    return (
        <div className={cx('relative', className)} style={style}>
            {/* Main carousel container */}
            <div
                className={cx(
                    'relative overflow-hidden rounded-lg bg-gray-200',
                    sizeMap[size] || sizeMap.md
                )}
                onTransitionEnd={handleTransitionEnd}
            >
                {/* Content */}
                <div className="h-full w-full">
                    {renderItem ? (
                        renderItem(currentItem, activeIndex)
                    ) : typeof currentItem === 'string' ? (
                        <img src={currentItem} alt="carousel item" className="h-full w-full object-cover" />
                    ) : currentItem.image ? (
                        <img src={currentItem.image} alt={currentItem.alt || 'carousel item'} className="h-full w-full object-cover" />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center text-white bg-gray-400">
                            {currentItem.title || 'Item ' + (activeIndex + 1)}
                        </div>
                    )}
                </div>

                {/* Previous Arrow */}
                {showArrows && (
                    <button
                        onClick={handlePrev}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
                        aria-label="Previous slide"
                    >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}

                {/* Next Arrow */}
                {showArrows && (
                    <button
                        onClick={handleNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
                        aria-label="Next slide"
                    >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Dot Indicators */}
            {showDots && items.length > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                    {items.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => handleDotClick(index)}
                            className={cx(
                                'h-2 rounded-full transition-all',
                                activeIndex === index ? 'w-6 bg-blue-600' : 'w-2 bg-gray-300 hover:bg-gray-400'
                            )}
                            aria-label={`Go to slide ${index + 1}`}
                            aria-current={activeIndex === index}
                        />
                    ))}
                </div>
            )}

            {/* Item counter */}
            <div className="mt-2 text-center text-sm text-gray-600">
                {activeIndex + 1} / {items.length}
            </div>
        </div>
    );
}